import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Spin } from 'antd';
import { Header } from './components/Layout/Header';
import { ResizableDashPanels } from './components/Layout/ResizableDashPanels';
import { Sidebar } from './components/Layout/Sidebar';
import { DetailPanel } from './components/Layout/DetailPanel';
import { PolicySidebar } from './components/Layout/PolicySidebar';
import { PolicyDetailPanel } from './components/Layout/PolicyDetailPanel';
import { DockingSidebar } from './components/Layout/DockingSidebar';
import { DockingDetailPanel } from './components/Layout/DockingDetailPanel';
import { ParkProfileSidebar } from './components/Layout/ParkProfileSidebar';
import { ParkProfilePage } from './components/ParkProfile/ParkProfilePage';
import { PARK_PROFILE_ORDER, hasParkProfile } from './constants/parkProfiles';
import { EchartsMap } from './components/Map/EchartsMap';
import { parksInProvince } from './components/Map/provinceAdcode';
import type { MapStatsScope } from './components/Map/types';
import { useParks } from './hooks/useParks';
import { usePolicies } from './hooks/usePolicies';
import { useDocking } from './hooks/useDocking';
import {
  applyDockingStatusFromRecords,
  filterParksByCondition,
  parksDataFilterToFilterCondition,
} from './utils/helpers';
import {
  countPoliciesByLevel,
  isPolicyFilterAtDefault,
  policiesInProvince,
} from './utils/policyHelpers';
import {
  DEFAULT_PARKS_DATA_FILTER,
  DEFAULT_POLICY_FILTER,
  type QueryMode,
  type ParkLevelFilter,
} from './types';
import { DEFAULT_DOCKING_FILTER } from './types/docking';

/**
 * 零碳园区：全屏 3D 地图底图 + 悬浮顶栏与左右面板
 */
function App(): React.ReactElement {
  const {
    allParks: rawAllParks,
    loading: parksLoading,
    error: parksError,
    filter: parkFilter,
    setFilter: setParkFilter,
  } = useParks();
  const {
    policies,
    allPolicies,
    loading: policiesLoading,
    error: policiesError,
    filter: policyFilter,
    setFilter: setPolicyFilter,
  } = usePolicies();
  const policyFilterRef = useRef(policyFilter);
  policyFilterRef.current = policyFilter;

  const docking = useDocking(rawAllParks);

  const dockedParkIds = useMemo(
    () => new Set(docking.records.map((record) => record.parkId)),
    [docking.records],
  );

  const allParks = useMemo(
    () => applyDockingStatusFromRecords(rawAllParks, dockedParkIds),
    [rawAllParks, dockedParkIds],
  );

  const parks = useMemo(() => {
    const condition = parksDataFilterToFilterCondition(parkFilter);
    return filterParksByCondition(allParks, condition);
  }, [allParks, parkFilter]);

  const [queryMode, setQueryMode] = useState<QueryMode>('parks');
  const [selectedParkId, setSelectedParkId] = useState<string | null>(null);
  const [selectedPolicyId, setSelectedPolicyId] = useState<string | null>(null);
  const [profileParkId, setProfileParkId] = useState<string | null>(null);
  const [profileKeyword, setProfileKeyword] = useState('');
  const [profileLevel, setProfileLevel] = useState<ParkLevelFilter>('all');
  /** 仅侧栏列表点选政策时递增，供地图组件区分「列表下钻」与「聚合点仅选中」 */
  const [policySidebarSelectTick, setPolicySidebarSelectTick] = useState(0);
  const [mapStatsScope, setMapStatsScope] = useState<MapStatsScope>({
    mode: 'nationalOverview',
    provinceAdcode: null,
  });

  /** 与地图视图一致：省下钻时列表、统计仅含当前省（仍先应用侧栏筛选条件） */
  const scopedParksForSidebar = useMemo(() => {
    if (
      mapStatsScope.mode === 'provinceDrill' &&
      mapStatsScope.provinceAdcode
    ) {
      return parksInProvince(parks, mapStatsScope.provinceAdcode);
    }
    return parks;
  }, [parks, mapStatsScope]);

  const scopedPoliciesForSidebar = useMemo(() => {
    let list = policies;
    if (
      mapStatsScope.mode === 'provinceDrill' &&
      mapStatsScope.provinceAdcode
    ) {
      list = policiesInProvince(list, mapStatsScope.provinceAdcode);
    }
    return list;
  }, [policies, mapStatsScope]);

  const profileParks = useMemo(() => {
    const profileNames = new Set<string>(PARK_PROFILE_ORDER);
    const keyword = profileKeyword.trim().toLowerCase();
    return allParks.filter((park) => {
      if (!profileNames.has(park.name)) return false;
      if (profileLevel !== 'all' && park.parkLevel !== profileLevel) return false;
      return !keyword || park.name.toLowerCase().includes(keyword);
    });
  }, [allParks, profileKeyword, profileLevel]);

  const scopedProfileParks = useMemo(() => {
    if (mapStatsScope.mode === 'provinceDrill' && mapStatsScope.provinceAdcode) {
      return parksInProvince(profileParks, mapStatsScope.provinceAdcode);
    }
    return profileParks;
  }, [profileParks, mapStatsScope]);

  const policySidebarStatistics = useMemo(
    () => countPoliciesByLevel(scopedPoliciesForSidebar),
    [scopedPoliciesForSidebar],
  );

  /** 与地图视图一致：工作管理省下钻时仅展示当前省的已对接园区 */
  const scopedDockingParkSummaries = useMemo(() => {
    if (
      mapStatsScope.mode !== 'provinceDrill' ||
      !mapStatsScope.provinceAdcode
    ) {
      return docking.parkSummaries;
    }
    const scopedParkIds = new Set(
      parksInProvince(
        docking.dockedParks,
        mapStatsScope.provinceAdcode,
      ).map((park) => park.id),
    );
    return docking.parkSummaries.filter((summary) =>
      scopedParkIds.has(summary.parkId),
    );
  }, [docking.dockedParks, docking.parkSummaries, mapStatsScope]);

  /** 顶部卡片同步统计当前地图省份；全国视图继续使用原统计 */
  const scopedDockingStatistics = useMemo(() => {
    if (
      mapStatsScope.mode !== 'provinceDrill' ||
      !mapStatsScope.provinceAdcode
    ) {
      return docking.statistics;
    }

    const scopedAllParks = parksInProvince(
      allParks,
      mapStatsScope.provinceAdcode,
    );
    const dockedNational = scopedDockingParkSummaries.filter(
      (summary) => summary.parkLevel === 'national',
    ).length;
    const dockedProvincial = scopedDockingParkSummaries.filter(
      (summary) => summary.parkLevel === 'provincial',
    ).length;
    const dockedPending = scopedDockingParkSummaries.filter(
      (summary) => summary.parkLevel === 'pending',
    ).length;

    return {
      dockedNational,
      dockedProvincial,
      dockedPending,
      totalNational: scopedAllParks.filter(
        (park) => park.parkLevel === 'national',
      ).length,
      totalProvincial: scopedAllParks.filter(
        (park) => park.parkLevel === 'provincial',
      ).length,
      totalPending: scopedAllParks.filter(
        (park) => park.parkLevel === 'pending',
      ).length,
    };
  }, [allParks, docking.statistics, mapStatsScope, scopedDockingParkSummaries]);

  const handleSelectPark = useCallback((parkId: string) => {
    setSelectedParkId(parkId);
  }, []);

  const dockingSetSelectedParkId = docking.setSelectedParkId;
  const dockingSelectedParkId = docking.selectedParkId;
  const handleSelectDockingPark = useCallback((parkId: string) => {
    dockingSetSelectedParkId(parkId);
  }, [dockingSetSelectedParkId]);

  const handleSelectPolicyFromSidebar = useCallback((policyId: string) => {
    setSelectedPolicyId(policyId);
    if (!isPolicyFilterAtDefault(policyFilterRef.current)) {
      setPolicySidebarSelectTick((n) => n + 1);
    }
  }, []);

  const handleSelectPolicyFromMap = useCallback((policyId: string) => {
    setSelectedPolicyId(policyId);
  }, []);

  useEffect(() => {
    if (!selectedParkId) return;
    const stillExists = parks.some((p) => p.id === selectedParkId);
    if (!stillExists) {
      setSelectedParkId(null);
    }
  }, [parks, selectedParkId]);

  useEffect(() => {
    if (!selectedParkId) return;
    if (
      mapStatsScope.mode !== 'provinceDrill' ||
      !mapStatsScope.provinceAdcode
    ) {
      return;
    }
    const inCurrentProvinceView = scopedParksForSidebar.some(
      (p) => p.id === selectedParkId,
    );
    if (!inCurrentProvinceView) {
      setSelectedParkId(null);
    }
  }, [selectedParkId, mapStatsScope, scopedParksForSidebar]);

  useEffect(() => {
    if (!selectedPolicyId) return;
    // 地图聚合点选中可不在当前侧栏筛选结果内，仅在全量数据中不存在时清空
    const stillExists = allPolicies.some(
      (policy) => policy.id === selectedPolicyId,
    );
    if (!stillExists) {
      setSelectedPolicyId(null);
    }
  }, [allPolicies, selectedPolicyId]);

  useEffect(() => {
    if (!dockingSelectedParkId) return;
    const inCurrentMapScope = scopedDockingParkSummaries.some(
      (summary) => summary.parkId === dockingSelectedParkId,
    );
    if (!inCurrentMapScope) {
      dockingSetSelectedParkId(null);
    }
  }, [
    dockingSelectedParkId,
    dockingSetSelectedParkId,
    scopedDockingParkSummaries,
  ]);

  const activeLoading =
    queryMode === 'parks' || queryMode === 'profiles' ? parksLoading :
    queryMode === 'policies' ? policiesLoading :
    parksLoading || docking.loading;
  const activeError =
    queryMode === 'parks' || queryMode === 'profiles' ? parksError :
    queryMode === 'policies' ? policiesError :
    parksError || docking.error;
  const showChrome = !activeError;

  /** 右侧详情面板宽度 + 边距，与 ResizableDashPanels 同步，避免遮挡地图按钮 */
  const [mapRightChromeInsetPx, setMapRightChromeInsetPx] = useState(
    316,
  );

  const profilePark = profileParkId
    ? allParks.find((park) => park.id === profileParkId)
    : undefined;

  if (profilePark && hasParkProfile(profilePark.name)) {
    return (
      <ParkProfilePage
        park={profilePark}
        onBack={() => setProfileParkId(null)}
      />
    );
  }

  return (
    <div className="app-shell app-shell--map">
      <div className="app-map-layer">
        {!activeError && (
          <EchartsMap
            parks={queryMode === 'parks' && !parksLoading ? parks : queryMode === 'docking' && !parksLoading ? docking.dockedParks : queryMode === 'profiles' && !parksLoading ? profileParks : []}
            selectedParkId={queryMode === 'parks' ? selectedParkId ?? undefined : queryMode === 'docking' ? docking.selectedParkId ?? undefined : undefined}
            onSelectPark={
              queryMode === 'parks' ? handleSelectPark : queryMode === 'docking' ? handleSelectDockingPark : queryMode === 'profiles' ? setProfileParkId : handleSelectPolicyFromMap
            }
            mapQueryMode={queryMode === 'policies' ? 'policies' : 'parks'}
            policyFilter={queryMode === 'policies' ? policyFilter : undefined}
            policiesForMap={queryMode === 'policies' && !policiesLoading ? policies : []}
            selectedPolicyId={
              queryMode === 'policies' ? selectedPolicyId ?? undefined : undefined
            }
            policySidebarSelectTick={
              queryMode === 'policies' ? policySidebarSelectTick : undefined
            }
            onPolicyMapDrillProvinceName={(name) =>
              setPolicyFilter((prev) => ({
                ...prev,
                // 下钻后保留省级+市级，避免省级聚合点被 level:city 过滤掉
                level: 'all',
                province: name,
                city: undefined,
              }))
            }
            onPolicyMapSelectCityName={(cityName) =>
              setPolicyFilter((prev) => ({
                ...prev,
                city: cityName,
              }))
            }
            onMapStatsScopeChange={setMapStatsScope}
            onBackToNational={() => {
              if (queryMode === 'parks') {
                setSelectedParkId(null);
                return;
              }
              if (queryMode === 'profiles') return;
              if (queryMode === 'docking') {
                docking.setSelectedParkId(null);
                return;
              }
              setSelectedPolicyId(null);
            }}
            onDrillProvince={() => {
              if (queryMode === 'parks') {
                setSelectedParkId(null);
                return;
              }
              if (queryMode === 'profiles') return;
              if (queryMode === 'docking') {
                docking.setSelectedParkId(null);
                return;
              }
              setSelectedPolicyId(null);
            }}
            onRestoreDefaults={() => {
              if (queryMode === 'parks') {
                setParkFilter(DEFAULT_PARKS_DATA_FILTER);
                setSelectedParkId(null);
                return;
              }
              if (queryMode === 'docking') {
                docking.setSelectedParkId(null);
                docking.setFilter(DEFAULT_DOCKING_FILTER);
                return;
              }
              if (queryMode === 'profiles') {
                setProfileKeyword('');
                setProfileLevel('all');
                return;
              }
              setPolicyFilter(DEFAULT_POLICY_FILTER);
              setSelectedPolicyId(null);
            }}
            layoutChromeRightInsetPx={mapRightChromeInsetPx}
          />
        )}
        {activeLoading && (
          <div className="app-map-overlay app-map-overlay--loading">
            <Spin
              size="large"
              tip={queryMode === 'parks' ? '加载园区数据…' : queryMode === 'policies' ? '加载政策数据…' : '加载对接数据…'}
            />
          </div>
        )}
        {activeError && (
          <div className="app-map-overlay app-map-overlay--error">
            <Alert type="error" message="数据加载失败" description={activeError} showIcon />
          </div>
        )}
      </div>

      <Header queryMode={queryMode} onQueryModeChange={setQueryMode} />

      {showChrome && (
        <ResizableDashPanels
          leftDefaultWidth={queryMode === 'policies' ? 420 : queryMode === 'docking' ? 380 : 320}
          onChromeRightInsetPx={setMapRightChromeInsetPx}
          left={
            queryMode === 'parks' ? (
              <Sidebar
                parks={parksLoading ? [] : scopedParksForSidebar}
                filter={parkFilter}
                setFilter={setParkFilter}
                selectedParkId={selectedParkId ?? undefined}
                onSelectPark={handleSelectPark}
              />
            ) : queryMode === 'profiles' ? (
              <ParkProfileSidebar
                parks={scopedProfileParks}
                keyword={profileKeyword}
                level={profileLevel}
                onKeywordChange={setProfileKeyword}
                onLevelChange={setProfileLevel}
                onOpenProfile={setProfileParkId}
              />
            ) : queryMode === 'docking' ? (
              <DockingSidebar
                statistics={scopedDockingStatistics}
                parkSummaries={scopedDockingParkSummaries}
                filter={docking.filter}
                setFilter={docking.setFilter}
                selectedParkId={docking.selectedParkId}
                onSelectPark={handleSelectDockingPark}
              />
            ) : (
              <PolicySidebar
                policies={policiesLoading ? [] : scopedPoliciesForSidebar}
                allPolicies={allPolicies}
                statistics={policySidebarStatistics}
                filter={policyFilter}
                setFilter={setPolicyFilter}
                selectedPolicyId={selectedPolicyId ?? undefined}
                onSelectPolicy={handleSelectPolicyFromSidebar}
              />
            )
          }
          right={queryMode === 'profiles' ? undefined :
            queryMode === 'parks' ? (
              <DetailPanel
                selectedParkId={selectedParkId ?? undefined}
                parks={parksLoading ? [] : allParks}
                onOpenProfile={setProfileParkId}
              />
            ) : queryMode === 'docking' ? (
              <DockingDetailPanel
                parks={allParks}
                selectedParkId={docking.selectedParkId}
                selectedParkRecords={docking.selectedParkRecords}
                onSave={docking.saveRecord}
                onDelete={docking.deleteRecord}
              />
            ) : (
              <PolicyDetailPanel
                selectedPolicyId={selectedPolicyId ?? undefined}
                policies={policiesLoading ? [] : allPolicies}
              />
            )
          }
        />
      )}
    </div>
  );
}

export default App;
